import { getNumberOrZero } from '@/common/utils';
import { ErrorAlert, InfoAlert } from '@/components/alerts';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { Form } from '@/components/forms';
import { useCurrentUser, useScanResult } from '@/hooks';
import { usePrizeEntryCount } from '@/hooks/use-prize-entry-count';
import { PrimaryLayout } from '@/layouts/primary';
import { EnterCodeRouteProps } from '@/routes/_app/enter-code';
import { Flex, Text, TextInput } from '@mantine/core';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';
import { useScanAssetFromUserManuallyMutation } from './graphql.generated';
import styles from './styles.module.css';

const schema = yup.object({
	code: yup.string().required('Code is required'),
});

type EnterCodeFormValues = {
	code: string;
};

const routeApi = getRouteApi('/_app/enter-code');

export const EnterCodePage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { refetch: refetchPrizeEntryCount } = usePrizeEntryCount();
	const { code } = routeApi.useSearch() as EnterCodeRouteProps;

	const initialValues: EnterCodeFormValues = useMemo(() => ({ code: code ?? '' }), [code]);

	const goToBagScanned = useCallback(
		(entriesGained: number) => navigate({ to: '/bag-scanned', search: { entriesGained } }),
		[navigate]
	);
	const [scanAssetFromUserManually, { loading }] = useScanAssetFromUserManuallyMutation();
	const { scanError, setApiErrors } = useScanResult();

	const handleSubmit = useCallback(
		async (values: EnterCodeFormValues) => {
			try {
				if (!currentUser?.id) {
					throw new Error('User is not authenticated');
				}

				const { data, errors } = await scanAssetFromUserManually({
					variables: {
						input: {
							scannableCode: values.code,
							createdByUser: currentUser.id,
						},
					},
				});

				setApiErrors(errors);
				if (errors === undefined) {
					await refetchPrizeEntryCount();
					goToBagScanned(getNumberOrZero(data?.scanAssetFromUser?.entriesGained));
				}
			} catch (error) {
				console.error(error);
				setApiErrors([error as Error]);
			}
		},
		[
			currentUser?.id,
			scanAssetFromUserManually,
			setApiErrors,
			goToBagScanned,
			refetchPrizeEntryCount,
		]
	);

	const formRef = useRef<FormikProps<EnterCodeFormValues>>(null);
	const submitForm = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	useEffect(() => {
		if (code && currentUser?.id) {
			submitForm();
		}
	}, [code, currentUser?.id, submitForm]);

	return (
		<PrimaryLayout
			header={{ title: 'Enter Code Manually', backTo: '/home' }}
			footer={{
				children: (
					<StandardButton loading={loading} onClick={submitForm}>
						Submit
					</StandardButton>
				),
			}}
		>
			<ContentPanel justify="center" flex={1}>
				<Flex
					direction="column"
					gap="md"
					align="center"
					justify="center"
					h="100%"
					w="100%"
					flex={1}
				>
					<InfoAlert>
						<Text size="sm" fw="600">
							The characters to type can be found just below the QR code on your Curby bag or tag.
							It should look something like <b>U1AtTi1CLTEtMQ</b>
						</Text>
					</InfoAlert>
					<Form
						onSubmit={handleSubmit}
						initialValues={initialValues}
						validationSchema={schema}
						innerRef={formRef}
					>
						{({ getFieldProps, errors }) => (
							<TextInput placeholder="Enter Code" {...getFieldProps('code')} error={errors.code} />
						)}
					</Form>
					{/* TODO: The form is no longer centered if an alert is shown */}
					{scanError && (
						<ErrorAlert p="xs">
							<Text size="xs" fw={400}>
								{scanError}. If you think this is an error, please{' '}
								<Link to="/report-issue" className={styles.link}>
									report an issue
								</Link>
								.
							</Text>
						</ErrorAlert>
					)}
				</Flex>
			</ContentPanel>
		</PrimaryLayout>
	);
};
